import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";

export interface HAConfirmationDialogPayloadEntry {
  key: string;
  value: string;
}

export interface HAConfirmationDialogDetails {
  entityId: string;
  domain: string;
  service: string;
  serviceLabel: string;
  safetyNote: string;
  payloadEntries: HAConfirmationDialogPayloadEntry[];
  payloadNotice: string | null;
}

interface HAConfirmationDialogProps {
  open: boolean;
  details: HAConfirmationDialogDetails | null;
  isExecuting: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

export function HAConfirmationDialog({
  open,
  details,
  isExecuting,
  onCancel,
  onConfirm,
}: HAConfirmationDialogProps) {
  return (
    <AlertDialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) {
          onCancel();
        }
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Confirm Home Assistant Action</AlertDialogTitle>
          <AlertDialogDescription className="space-y-3">
            <span className="block">
              This action needs explicit review before TerraMatrix can run it.
            </span>
            {details ? (
              <>
                <span className="block">Service: {details.serviceLabel}</span>
                <span className="block">Entity: {details.entityId}</span>
                <span className="block">Domain: {details.domain}</span>
                <span className="block">
                  Requested service: {details.service}
                </span>
                {details.payloadEntries.length > 0 ? (
                  <span className="block">
                    <span className="mb-2 block font-medium text-foreground">
                      Approved payload
                    </span>
                    <span className="block rounded-md border bg-muted/30 p-3 text-sm text-foreground">
                      <span className="grid grid-cols-[auto,1fr] gap-x-3 gap-y-1">
                        {details.payloadEntries.map((entry) => (
                          <span key={entry.key} className="contents">
                            <span className="font-mono text-xs text-muted-foreground">
                              {entry.key}
                            </span>
                            <span className="font-mono text-xs">
                              {entry.value}
                            </span>
                          </span>
                        ))}
                      </span>
                    </span>
                  </span>
                ) : null}
                {details.payloadNotice ? (
                  <span className="block">{details.payloadNotice}</span>
                ) : null}
                <span className="block">{details.safetyNote}</span>
              </>
            ) : null}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={isExecuting}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={onConfirm}
            disabled={isExecuting}
            aria-busy={isExecuting}
          >
            {isExecuting ? "Running..." : "Run Service"}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
